import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { questionSteps, DEFAULT_ELECTRICITY_RATE } from '../../data/questionnaireData';
import ElectricityBillStep from './QuestionSteps/ElectricityBillStep';
import SystemTypeStep from './QuestionSteps/SystemTypeStep';
import SubsidyInfoStep from './QuestionSteps/SubsidyInfoStep';
import ResultsDisplay from './ResultsDisplay';
import './QuestionnairePage.css';

const QuestionnairePage = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [responses, setResponses] = useState({
    monthlyBill: null,
    electricityRate: DEFAULT_ELECTRICITY_RATE, // Default rate: ₹8.5/unit
    systemType: null,
    hasSeenSubsidy: false
  });

  const totalSteps = questionSteps.length;
  const isResultsStep = currentStep > totalSteps;

  const handleNext = () => {
    if (currentStep === 3) {
      handleResponse('hasSeenSubsidy', true);
    }
    if (currentStep <= totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleResponse = (key, value) => {
    setResponses(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleStartOver = () => {
    setCurrentStep(1);
    setResponses({
      monthlyBill: null,
      electricityRate: DEFAULT_ELECTRICITY_RATE,
      systemType: null,
      hasSeenSubsidy: false
    });
  };

  const handleGoHome = () => {
    if (onClose) {
      onClose();
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return responses.monthlyBill !== null && responses.monthlyBill > 0;
      case 2:
        return responses.systemType !== null;
      case 3:
        return true; // Subsidy info is just informational
      default:
        return false;
    }
  };

  const renderStep = () => {
    if (isResultsStep) {
      return <ResultsDisplay responses={responses} onStartOver={handleStartOver} onGoHome={handleGoHome} />;
    }

    switch (currentStep) {
      case 1:
        return (
          <ElectricityBillStep
            value={responses.monthlyBill}
            onChange={(value) => handleResponse('monthlyBill', value)}
          />
        );
      case 2:
        return (
          <SystemTypeStep
            selected={responses.systemType}
            onSelect={(value) => handleResponse('systemType', value)}
          />
        );
      case 3:
        return (
          <SubsidyInfoStep />
        );
      default:
        return null;
    }
  };

  return (
    <div className="questionnaire-page">
      {/* Navigation Header */}
      <motion.nav 
        className="questionnaire-nav"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="questionnaire-nav-container">
          <div className="questionnaire-nav-logo" onClick={handleGoHome}>
            <img 
              src="/images/logo.png" 
              alt="Adityam URJA Logo" 
              className="questionnaire-logo-image"
            />
          </div>
          
          {!isResultsStep && (
            <div className="questionnaire-header-progress">
              <div className="questionnaire-progress-bar">
                <div
                  className="questionnaire-progress-fill"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
              <div className="questionnaire-step-info">
                <span className="questionnaire-step-number">Step {currentStep}</span>
                <span className="questionnaire-step-separator">/</span>
                <span className="questionnaire-step-total">{totalSteps}</span>
              </div>
            </div>
          )}
          
          <button className="questionnaire-close-btn" onClick={handleGoHome}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </motion.nav>

      {/* Main Content */}
      <div className="questionnaire-page-content">
        <div className="questionnaire-main-container">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              className="questionnaire-step-wrapper"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>

          {!isResultsStep && (
            <div className="questionnaire-navigation">
              {currentStep > 1 && (
                <button className="questionnaire-btn-back" onClick={handleBack}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  Back
                </button>
              )}
              <button
                className={`questionnaire-btn-next ${!canProceed() ? 'disabled' : ''}`}
                onClick={handleNext}
                disabled={!canProceed()}
              >
                {currentStep === totalSteps ? 'Get My Plan' : 'Continue'}
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Background Pattern */}
      <div className="questionnaire-bg-pattern"></div>
    </div>
  );
};

export default QuestionnairePage;

